import type { AxiosInstance, AxiosRequestHeaders, AxiosResponse, Method } from "axios"
import axios from "axios"
import { config } from "../config"
import type { AppState, Licence } from "../redux"
import { bosAtlasStore } from "../redux/Store"
import { httpDelete, httpGet, httpPost, httpPut, httpRequestComplete } from "../redux/http/http.actions"
import type { HttpMethod, HttpResponse } from "../redux/http/http.types"

export const httpClient: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true
})

export async function authorizedFetch(url: string): Promise<Response> {
  const requestInit: RequestInit = {
    method: "GET",
    headers: await getRequestHeaders(),
    credentials: "include"
  }
  return await fetch(`${config.apiUrl}${url}`, requestInit)
}

export async function get<T>(
  sectionName: string,
  url: string,
  abortController?: AbortController
): Promise<HttpResponse<T>> {
  const requestInit: RequestInit = {
    method: "GET",
    headers: await getRequestHeaders()
  }

  bosAtlasStore.dispatch(httpGet(sectionName, url))

  const result: HttpResponse<T> = await typedFetch<T>(url, requestInit, abortController)

  bosAtlasStore.dispatch(httpRequestComplete(sectionName, url))

  return result
}

export async function post<T, S>(sectionName: string, url: string, data: S): Promise<HttpResponse<T>> {
  const requestInit: RequestInit = {
    method: "POST",
    headers: await getRequestHeaders(),
    body: JSON.stringify(data)
  }

  bosAtlasStore.dispatch(httpPost(sectionName, url))
  const result: HttpResponse<T> = await typedFetch<T>(url, requestInit)
  bosAtlasStore.dispatch(httpRequestComplete(sectionName, url))

  return result
}

export async function put<S>(sectionName: string, url: string, data: S): Promise<HttpResponse> {
  const requestInit: RequestInit = {
    method: "PUT",
    headers: await getRequestHeaders(),
    body: JSON.stringify(data)
  }

  bosAtlasStore.dispatch(httpPut(sectionName))
  const response: HttpResponse = await typedFetch(url, requestInit)
  bosAtlasStore.dispatch(httpRequestComplete(sectionName))

  return response
}

export async function remove(sectionName: string, url: string): Promise<HttpResponse> {
  const method: HttpMethod = "DELETE"
  const requestInit: RequestInit = {
    method,
    headers: await getRequestHeaders()
  }

  bosAtlasStore.dispatch(httpDelete(sectionName))
  const response: HttpResponse = await typedFetch(url, requestInit)
  bosAtlasStore.dispatch(httpRequestComplete(sectionName))

  return response
}

export async function upload(url: string, file: File): Promise<HttpResponse<string>> {
  const formData = new FormData()
  formData.append("file", file, file.name)
  const requestInit: RequestInit = {
    method: "POST",
    headers: await getRequestHeaders()
  }
  return await sendRequest(url, requestInit, formData)
}

async function typedFetch<T>(
  request: RequestInfo,
  requestInit: RequestInit,
  abortController?: AbortController
): Promise<HttpResponse<T>> {
  const response: AxiosResponse = await sendRequest(request, requestInit, undefined, abortController)
  let data: T | undefined
  try {
    data = (await response.data) as T
  } catch {
    data = undefined
  } finally {
    // biome-ignore lint/correctness/noUnsafeFinally: TODO fix in later PR
    return {
      ...response,
      data
    }
  }
}

async function getRequestHeaders(): Promise<Headers> {
  return new Headers({
    "Content-Type": "application/json",
    Accept: "application/json",
    Licence: getSelectedLicenceId()
  })
}

function getSelectedLicenceId(): string {
  const state: AppState = bosAtlasStore.getState()
  const selectedLicence: Licence | undefined = state.user.selectedLicence
  const localSelectedLicence: string | null = localStorage.getItem(config.selectedLicenceLocalstorageKey)
  return localSelectedLicence !== null ? localSelectedLicence : selectedLicence ? selectedLicence.ean : ""
}

async function sendRequest(
  request: RequestInfo,
  requestInit: RequestInit,
  formData?: FormData,
  abortController?: AbortController
) {
  const method: Method = requestInit.method as Method
  const headers: AxiosRequestHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Licence: getSelectedLicenceId()
  }
  return await httpClient({
    method,
    url: request as string,
    data: formData ? formData : requestInit.body,
    headers,
    signal: abortController?.signal
  })
}
